/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const esUY: LocalizationResource = {
  locale: 'es-UY',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copiar y cerrar',
      formHint: 'Por razones de seguridad, no podrás verlo de nuevo más tarde.',
      formTitle: 'Copia tu clave API "{{name}}" ahora',
    },
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: 'Atrás',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: 'Predeterminado',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Otro dispositivo de suplantación',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'Principal',
  badge__renewsAt: undefined,
  badge__requiresAction: 'Requiere acción',
  badge__startsAt: undefined,
  badge__thisDevice: 'Este dispositivo',
  badge__trialEndsAt: undefined,
  badge__unverified: 'No verificado',
  badge__upcomingPlan: undefined,
  badge__userDevice: 'Dispositivo del usuario',
  badge__you: 'Vos',
  billing: {
    addPaymentMethod__label: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedMonthlyOnly: undefined,
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    keepFreeTrial: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    paymentMethods__label: undefined,
    popular: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  createOrganization: {
    formButtonSubmit: 'Crear organización',
    invitePage: {
      formButtonReset: 'Omitir',
    },
    title: 'Crear organización',
  },
  dates: {
    lastDay: "Ayer a las {{ date | timeString('es-UY') }}",
    next6Days: "{{ date | weekday('es-UY','long') }} a las {{ date | timeString('es-UY') }}",
    nextDay: "Mañana a las {{ date | timeString('es-UY') }}",
    numeric: "{{ date | numeric('es-UY') }}",
    previous6Days: "El pasado {{ date | weekday('es-UY','long') }} a las {{ date | timeString('es-UY') }}",
    sameDay: "Hoy a las {{ date | timeString('es-UY') }}",
  },
  dividerText: 'o',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'Usar otro método',
  footerPageLink__help: 'Ayuda',
  footerPageLink__privacy: 'Privacidad',
  footerPageLink__terms: 'Términos',
  formButtonPrimary: 'Continuar',
  formButtonPrimary__verify: 'Verificar',
  formFieldAction__forgotPassword: '¿Olvidaste tu contraseña?',
  formFieldError__matchingPasswords: 'Las contraseñas coinciden.',
  formFieldError__notMatchingPasswords: 'Las contraseñas no coinciden.',
  formFieldError__verificationLinkExpired:
    'El enlace de verificación ha expirado. Por favor, solicitá un nuevo enlace.',
  formFieldHintText__optional: 'Opcional',
  formFieldHintText__slug:
    'Un slug es una identificación legible por humanos que debe ser única. A menudo se utiliza en URLs.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: 'Ingresá el código de respaldo',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Eliminar cuenta',
  formFieldInputPlaceholder__emailAddress: 'Ingresá tu dirección de correo electrónico',
  formFieldInputPlaceholder__emailAddress_username: 'Ingresá correo electrónico o nombre de usuario',
  formFieldInputPlaceholder__emailAddresses: 'ejemplo@email.com, ejemplo2@email.com',
  formFieldInputPlaceholder__firstName: 'Nombre',
  formFieldInputPlaceholder__lastName: 'Apellido',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'vos@ejemplo.com',
  formFieldInputPlaceholder__organizationName: 'Nombre de la organización',
  formFieldInputPlaceholder__organizationSlug: 'mi-org',
  formFieldInputPlaceholder__password: 'Ingresá tu contraseña',
  formFieldInputPlaceholder__phoneNumber: 'Ingresá tu número de teléfono',
  formFieldInputPlaceholder__username: undefined,
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'Clave API',
  formFieldLabel__apiKeyDescription: 'Descripción',
  formFieldLabel__apiKeyExpiration: 'Expiración',
  formFieldLabel__apiKeyName: 'Nombre de clave secreta',
  formFieldLabel__automaticInvitations: 'Habilitar invitaciones automáticas para este dominio',
  formFieldLabel__backupCode: 'Código de respaldo',
  formFieldLabel__confirmDeletion: 'Confirmación',
  formFieldLabel__confirmPassword: 'Confirmar contraseña',
  formFieldLabel__currentPassword: 'Contraseña actual',
  formFieldLabel__emailAddress: 'Correo electrónico',
  formFieldLabel__emailAddress_username: 'Correo electrónico o nombre de usuario',
  formFieldLabel__emailAddresses: 'Correos electrónicos',
  formFieldLabel__firstName: 'Nombre',
  formFieldLabel__lastName: 'Apellido',
  formFieldLabel__newPassword: 'Nueva contraseña',
  formFieldLabel__organizationDomain: 'Dominio',
  formFieldLabel__organizationDomainDeletePending: 'Eliminar invitaciones y sugerencias pendientes',
  formFieldLabel__organizationDomainEmailAddress: 'Correo electrónico de verificación',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Ingresá un correo electrónico bajo este dominio para recibir un código y verificar el dominio.',
  formFieldLabel__organizationName: 'Nombre',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Nombre de la clave de acceso',
  formFieldLabel__password: 'Contraseña',
  formFieldLabel__phoneNumber: 'Número de teléfono',
  formFieldLabel__role: 'Rol',
  formFieldLabel__signOutOfOtherSessions: 'Cerrar sesión en todos los demás dispositivos',
  formFieldLabel__username: 'Nombre de usuario',
  impersonationFab: {
    action__signOut: 'Cerrar sesión',
    title: 'Conectado como {{identifier}}',
  },
  lastAuthenticationStrategy: 'Último uso',
  maintenanceMode: 'Actualmente estamos en mantenimiento, pero no te preocupes, no tomará más que unos minutos.',
  membershipRole__admin: 'Administrador',
  membershipRole__basicMember: 'Miembro',
  membershipRole__guestMember: 'Invitado',
  organizationList: {
    action__createOrganization: 'Crear organización',
    action__invitationAccept: 'Unirse',
    action__suggestionsAccept: 'Solicitar unirse',
    createOrganization: 'Crear organización',
    invitationAcceptedLabel: 'Unido',
    subtitle: 'para continuar a {{applicationName}}',
    suggestionsAcceptedLabel: 'Aprobación pendiente',
    title: 'Elegí una cuenta',
    titleWithoutPersonal: 'Elegí una organización',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'Invitaciones automáticas',
    badge__automaticSuggestion: 'Sugerencias automáticas',
    badge__manualInvitation: 'Sin inscripción automática',
    badge__unverified: 'No verificado',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'Agregá el dominio para verificar. Los usuarios con correos electrónicos de este dominio pueden unirse automáticamente a la organización o solicitar unirse.',
      title: 'Agregar dominio',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'No se pudieron enviar las invitaciones. Ya existen invitaciones pendientes para los siguientes correos electrónicos: {{email_addresses}}.',
      formButtonPrimary__continue: 'Enviar invitaciones',
      selectDropdown__role: 'Seleccionar rol',
      subtitle: 'Ingresá o pegá uno o más correos electrónicos, separados por espacios o comas.',
      successMessage: 'Invitaciones enviadas con éxito',
      title: 'Invitar nuevos miembros',
    },
    membersPage: {
      action__invite: 'Invitar',
      action__search: 'Buscar',
      activeMembersTab: {
        menuAction__remove: 'Eliminar miembro',
        tableHeader__actions: 'Acciones',
        tableHeader__joined: 'Se unió',
        tableHeader__role: 'Rol',
        tableHeader__user: 'Usuario',
      },
      detailsTitle__emptyRow: 'No hay miembros para mostrar',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Invitá a usuarios conectando un dominio de correo electrónico con tu organización. Cualquiera que se registre con un correo que coincida podrá unirse a la organización en cualquier momento.',
          headerTitle: 'Invitaciones automáticas',
          primaryButton: 'Gestionar dominios verificados',
        },
        table__emptyRow: 'No hay invitaciones para mostrar',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Revocar invitación',
        tableHeader__invited: 'Invitado',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Los usuarios que se registren con un correo que coincida podrán ver una sugerencia para solicitar unirse a tu organización.',
          headerTitle: 'Sugerencias automáticas',
          primaryButton: 'Gestionar dominios verificados',
        },
        menuAction__approve: 'Aprobar',
        menuAction__reject: 'Rechazar',
        tableHeader__requested: 'Acceso solicitado',
        table__emptyRow: 'No hay solicitudes para mostrar',
      },
      start: {
        headerTitle__invitations: 'Invitaciones',
        headerTitle__members: 'Miembros',
        headerTitle__requests: 'Solicitudes',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'Gestioná tu organización.',
      general: 'General',
      members: 'Miembros',
      title: 'Organización',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Escribí "{{organizationName}}" abajo para continuar.',
          messageLine1: '¿Estás seguro de que querés eliminar esta organización?',
          messageLine2: 'Esta acción es permanente e irreversible.',
          successMessage: 'Has eliminado la organización.',
          title: 'Eliminar organización',
        },
        leaveOrganization: {
          actionDescription: 'Escribí "{{organizationName}}" abajo para continuar.',
          messageLine1:
            '¿Estás seguro de que querés abandonar esta organización? Perderás el acceso a la organización y a sus aplicaciones.',
          messageLine2: 'Esta acción es permanente e irreversible.',
          successMessage: 'Has abandonado la organización.',
          title: 'Abandonar organización',
        },
        title: 'Peligro',
      },
      domainSection: {
        menuAction__manage: 'Gestionar',
        menuAction__remove: 'Eliminar',
        menuAction__verify: 'Verificar',
        primaryButton: 'Agregar dominio',
        subtitle:
          'Permití que los usuarios se unan a la organización automáticamente o soliciten unirse en base a un dominio de correo verificado.',
        title: 'Dominios verificados',
      },
      successMessage: 'La organización ha sido actualizada.',
      title: 'Actualizar perfil',
    },
    removeDomainPage: {
      messageLine1: 'El dominio de correo {{domain}} será eliminado.',
      messageLine2: 'Los usuarios no podrán unirse automáticamente a la organización después de esto.',
      successMessage: '{{domain}} ha sido eliminado.',
      title: 'Eliminar dominio',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: 'Miembros',
      profileSection: {
        primaryButton: 'Actualizar perfil',
        title: 'Perfil de la organización',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Eliminar este dominio afectará a los usuarios invitados.',
        removeDomainActionLabel__remove: 'Eliminar dominio',
        removeDomainSubtitle: 'Eliminar este dominio de tus dominios verificados',
        removeDomainTitle: 'Eliminar dominio',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Los usuarios son invitados automáticamente a unirse a la organización al registrarse y pueden unirse en cualquier momento.',
        automaticInvitationOption__label: 'Invitaciones automáticas',
        automaticSuggestionOption__description:
          'Los usuarios reciben una sugerencia para solicitar unirse, pero deben ser aprobados por un administrador antes de poder unirse a la organización.',
        automaticSuggestionOption__label: 'Sugerencias automáticas',
        calloutInfoLabel: 'Cambiar el modo de inscripción solo afectará a los nuevos usuarios.',
        calloutInvitationCountLabel: 'Invitaciones pendientes enviadas a usuarios: {{count}}',
        calloutSuggestionCountLabel: 'Sugerencias pendientes enviadas a usuarios: {{count}}',
        manualInvitationOption__description: 'Los usuarios solo pueden ser invitados manualmente a la organización.',
        manualInvitationOption__label: 'Sin inscripción automática',
        subtitle: 'Elegí cómo los usuarios de este dominio pueden unirse a la organización.',
      },
      start: {
        headerTitle__danger: 'Peligro',
        headerTitle__enrollment: 'Opciones de inscripción',
      },
      subtitle: 'El dominio {{domain}} está verificado. Continuá seleccionando el modo de inscripción.',
      title: 'Actualizar {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Ingresá el código de verificación enviado a tu correo electrónico',
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'El dominio {{domainName}} necesita ser verificado por correo electrónico.',
      subtitleVerificationCodeScreen:
        'Se envió un código de verificación a {{emailAddress}}. Ingresá el código para continuar.',
      title: 'Verificar dominio',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'Crear organización',
    action__invitationAccept: 'Unirse',
    action__manageOrganization: 'Gestionar',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'Solicitar unirse',
    notSelected: 'Ninguna organización seleccionada',
    personalWorkspace: 'Cuenta personal',
    suggestionsAcceptedLabel: 'Aprobación pendiente',
  },
  paginationButton__next: 'Siguiente',
  paginationButton__previous: 'Anterior',
  paginationRowText__displaying: 'Mostrando',
  paginationRowText__of: 'de',
  reverification: {
    alternativeMethods: {
      actionLink: 'Obtener ayuda',
      actionText: '¿No tenés ninguno de estos?',
      blockButton__backupCode: 'Usar un código de respaldo',
      blockButton__emailCode: 'Enviar código por correo a {{identifier}}',
      blockButton__passkey: undefined,
      blockButton__password: 'Continuar con tu contraseña',
      blockButton__phoneCode: 'Enviar código SMS a {{identifier}}',
      blockButton__totp: 'Usar tu aplicación autenticadora',
      getHelp: {
        blockButton__emailSupport: 'Soporte por correo',
        content:
          'Si tenés problemas para verificar tu cuenta, enviános un correo y trabajaremos para restaurar el acceso lo antes posible.',
        title: 'Obtener ayuda',
      },
      subtitle: '¿Tenés problemas? Podés usar cualquiera de estos métodos para la verificación.',
      title: 'Usar otro método',
    },
    backupCodeMfa: {
      subtitle: 'Ingresá el código de respaldo que recibiste al configurar la autenticación de dos pasos',
      title: 'Ingresá un código de respaldo',
    },
    emailCode: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresá el código enviado a tu correo para continuar',
      title: 'Verificación requerida',
    },
    noAvailableMethods: {
      message: 'No se puede proceder con la verificación. No hay un factor de autenticación adecuado configurado',
      subtitle: 'Ocurrió un error',
      title: 'No se puede verificar tu cuenta',
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: 'Usar otro método',
      subtitle: 'Ingresá tu contraseña para continuar',
      title: 'Verificación requerida',
    },
    phoneCode: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresá el código enviado a tu teléfono para continuar',
      title: 'Verificación requerida',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresá el código enviado a tu teléfono para continuar',
      title: 'Verificación requerida',
    },
    totpMfa: {
      formTitle: 'Código de verificación',
      subtitle: 'Ingresá el código generado por tu aplicación autenticadora para continuar',
      title: 'Verificación requerida',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Agregar cuenta',
      action__signOutAll: 'Cerrar sesión en todas las cuentas',
      subtitle: 'Seleccioná la cuenta con la que deseas continuar.',
      title: 'Elegí una cuenta',
    },
    alternativeMethods: {
      actionLink: 'Obtener ayuda',
      actionText: '¿No tenés ninguno de estos?',
      blockButton__backupCode: 'Usar un código de respaldo',
      blockButton__emailCode: 'Enviar código por correo a {{identifier}}',
      blockButton__emailLink: 'Enviar enlace por correo a {{identifier}}',
      blockButton__passkey: 'Ingresá con tu clave de acceso',
      blockButton__password: 'Ingresá con tu contraseña',
      blockButton__phoneCode: 'Enviar código SMS a {{identifier}}',
      blockButton__totp: 'Usar tu aplicación autenticadora',
      getHelp: {
        blockButton__emailSupport: 'Soporte por correo',
        content:
          'Si tenés problemas para ingresar a tu cuenta, enviános un correo y trabajaremos para restaurar el acceso lo antes posible.',
        title: 'Obtener ayuda',
      },
      subtitle: '¿Tenés problemas? Podés usar cualquiera de estos métodos para ingresar.',
      title: 'Usar otro método',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: 'Tu código de respaldo es el que recibiste al configurar la autenticación de dos pasos.',
      title: 'Ingresá un código de respaldo',
    },
    emailCode: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Revisá tu correo',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Para continuar, abrí el enlace de verificación en el dispositivo y navegador desde el cual iniciaste sesión',
        title: 'El enlace de verificación no es válido para este dispositivo',
      },
      expired: {
        subtitle: 'Volvé a la pestaña original para continuar.',
        title: 'Este enlace de verificación ha expirado',
      },
      failed: {
        subtitle: 'Volvé a la pestaña original para continuar.',
        title: 'Este enlace de verificación no es válido',
      },
      formSubtitle: 'Usá el enlace de verificación enviado a tu correo',
      formTitle: 'Enlace de verificación',
      loading: {
        subtitle: 'Serás redirigido pronto',
        title: 'Iniciando sesión...',
      },
      resendButton: '¿No recibiste un enlace? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Revisá tu correo',
      unusedTab: {
        title: 'Podés cerrar esta pestaña',
      },
      verified: {
        subtitle: 'Serás redirigido pronto',
        title: 'Sesión iniciada con éxito',
      },
      verifiedSwitchTab: {
        subtitle: 'Volvé a la pestaña original para continuar',
        subtitleNewTab: 'Volvé a la nueva pestaña para continuar',
        titleNewTab: 'Sesión iniciada en otra pestaña',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'Código para restablecer la contraseña',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'para restablecer tu contraseña',
      subtitle_email: 'Primero, ingresá el código enviado a tu correo electrónico',
      subtitle_phone: 'Primero, ingresá el código enviado a tu teléfono',
      title: 'Restablecer contraseña',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Restablecer tu contraseña',
      label__alternativeMethods: 'O, ingresá con otro método',
      title: '¿Olvidaste tu contraseña?',
    },
    noAvailableMethods: {
      message: 'No se puede continuar con el inicio de sesión. No hay un factor de autenticación disponible.',
      subtitle: 'Ocurrió un error',
      title: 'No se puede iniciar sesión',
    },
    passkey: {
      subtitle:
        'Usar tu clave de acceso confirma que sos vos. Tu dispositivo puede solicitar tu huella, rostro o bloqueo de pantalla.',
      title: 'Usar tu clave de acceso',
    },
    password: {
      actionLink: 'Usar otro método',
      subtitle: 'Ingresá la contraseña asociada a tu cuenta',
      title: 'Ingresá tu contraseña',
    },
    passwordPwned: {
      title: 'Contraseña comprometida',
    },
    phoneCode: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Revisá tu teléfono',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Para continuar, ingresá el código enviado a tu teléfono',
      title: 'Revisá tu teléfono',
    },
    resetPassword: {
      formButtonPrimary: 'Restablecer contraseña',
      requiredMessage: 'Por razones de seguridad, es necesario restablecer tu contraseña.',
      successMessage: 'Tu contraseña se cambió con éxito. Iniciando sesión, por favor, esperá un momento.',
      title: 'Establecer nueva contraseña',
    },
    resetPasswordMfa: {
      detailsLabel: 'Necesitamos verificar tu identidad antes de restablecer tu contraseña.',
    },
    start: {
      actionLink: 'Registrate',
      actionLink__join_waitlist: 'Unirse a la lista de espera',
      actionLink__use_email: 'Usar correo',
      actionLink__use_email_username: 'Usar correo o nombre de usuario',
      actionLink__use_passkey: 'Usar clave de acceso en su lugar',
      actionLink__use_phone: 'Usar teléfono',
      actionLink__use_username: 'Usar nombre de usuario',
      actionText: '¿No tenés una cuenta?',
      actionText__join_waitlist: '¿Querés acceso anticipado?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '¡Bienvenido de nuevo! Por favor, ingresá para continuar',
      subtitleCombined: undefined,
      title: 'Iniciá sesión en {{applicationName}}',
      titleCombined: 'Continuar a {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Código de verificación',
      subtitle: 'Para continuar, ingresá el código generado por tu aplicación autenticadora',
      title: 'Verificación en dos pasos',
    },
  },
  signInEnterPasswordTitle: 'Ingresá tu contraseña',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'Iniciar sesión',
      actionText: '¿Ya tenés una cuenta?',
      subtitle: 'Completá los datos restantes para continuar.',
      title: 'Completá los campos faltantes',
    },
    emailCode: {
      formSubtitle: 'Ingresá el código de verificación enviado a tu correo electrónico',
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresá el código de verificación enviado a tu correo',
      title: 'Verificá tu correo',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Para continuar, abrí el enlace de verificación en el dispositivo y navegador desde el cual te registraste',
        title: 'El enlace de verificación no es válido para este dispositivo',
      },
      formSubtitle: 'Usá el enlace de verificación enviado a tu correo electrónico',
      formTitle: 'Enlace de verificación',
      loading: {
        title: 'Registrándose...',
      },
      resendButton: '¿No recibiste un enlace? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Verificá tu correo',
      verified: {
        title: 'Registro exitoso',
      },
      verifiedSwitchTab: {
        subtitle: 'Volvé a la nueva pestaña para continuar',
        subtitleNewTab: 'Volvé a la pestaña anterior para continuar',
        title: 'Correo verificado con éxito',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Acepto la {{ privacyPolicyLink || link("Política de Privacidad") }}',
        label__onlyTermsOfService: 'Acepto los {{ termsOfServiceLink || link("Términos de Servicio") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Acepto los {{ termsOfServiceLink || link("Términos de Servicio") }} y la {{ privacyPolicyLink || link("Política de Privacidad") }}',
      },
      continue: {
        subtitle: 'Por favor, leé y aceptá los términos para continuar',
        title: 'Consentimiento legal',
      },
    },
    phoneCode: {
      formSubtitle: 'Ingresá el código de verificación enviado a tu número de teléfono',
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresá el código enviado a tu teléfono',
      title: 'Verificá tu teléfono',
    },
    restrictedAccess: {
      actionLink: 'Iniciar sesión',
      actionText: '¿Ya tenés una cuenta?',
      blockButton__emailSupport: 'Soporte por correo',
      blockButton__joinWaitlist: 'Unirse a la lista de espera',
      subtitle:
        'Actualmente, los registros están deshabilitados. Si creés que deberías tener acceso, contactá al soporte.',
      subtitleWaitlist:
        'Actualmente, los registros están deshabilitados. Para ser el primero en saber cuando lancemos, unite a la lista de espera.',
      title: 'Acceso restringido',
    },
    start: {
      actionLink: 'Iniciar sesión',
      actionLink__use_email: 'Usar correo en su lugar',
      actionLink__use_phone: 'Usar teléfono en su lugar',
      actionText: '¿Ya tenés una cuenta?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '¡Bienvenido! Completá los datos para comenzar.',
      subtitleCombined: '¡Bienvenido! Completá los datos para comenzar.',
      title: 'Creá tu cuenta',
      titleCombined: 'Creá tu cuenta',
    },
  },
  socialButtonsBlockButton: 'Continuar con {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    chooseOrganization: {
      action__createOrganization: undefined,
      action__invitationAccept: undefined,
      action__suggestionsAccept: undefined,
      subtitle: undefined,
      suggestionsAcceptedLabel: undefined,
      title: undefined,
    },
    createOrganization: {
      formButtonReset: undefined,
      formButtonSubmit: undefined,
      formFieldInputPlaceholder__name: undefined,
      formFieldInputPlaceholder__slug: undefined,
      formFieldLabel__name: undefined,
      formFieldLabel__slug: undefined,
      subtitle: undefined,
      title: undefined,
    },
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} ya es miembro de la organización.',
    captcha_invalid:
      'El registro no se pudo completar debido a validaciones de seguridad fallidas. Por favor, actualizá la página para intentarlo de nuevo o contactá al soporte para más asistencia.',
    captcha_unavailable:
      'El registro no se pudo completar debido a la validación fallida contra bots. Por favor, actualizá la página para intentarlo de nuevo o contactá al soporte para más asistencia.',
    form_code_incorrect: undefined,
    form_identifier_exists__email_address: 'Este correo electrónico ya está en uso. Por favor, probá con otro.',
    form_identifier_exists__phone_number: 'Este número de teléfono ya está en uso. Por favor, probá con otro.',
    form_identifier_exists__username: 'Este nombre de usuario ya está en uso. Por favor, probá con otro.',
    form_identifier_not_found: 'No se encontró una cuenta con esos detalles.',
    form_param_format_invalid: 'El valor ingresado tiene un formato inválido. Por favor, verificá y corregí.',
    form_param_format_invalid__email_address: 'El correo electrónico debe ser válido.',
    form_param_format_invalid__phone_number: 'El número de teléfono debe estar en un formato internacional válido.',
    form_param_max_length_exceeded__first_name: 'El nombre no debe exceder los 256 caracteres.',
    form_param_max_length_exceeded__last_name: 'El apellido no debe exceder los 256 caracteres.',
    form_param_max_length_exceeded__name: 'El nombre no debe exceder los 256 caracteres.',
    form_param_nil: 'Este campo es obligatorio y no puede estar vacío.',
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: 'El valor ingresado es inválido. Por favor, corregilo.',
    form_password_incorrect: 'La contraseña ingresada es incorrecta. Por favor, intentá de nuevo.',
    form_password_length_too_short: 'Tu contraseña es demasiado corta. Debe tener al menos 8 caracteres.',
    form_password_not_strong_enough: 'Tu contraseña no es lo suficientemente fuerte.',
    form_password_pwned:
      'Esta contraseña se encontró en una filtración y no se puede usar. Por favor, probá con otra contraseña.',
    form_password_pwned__sign_in:
      'Esta contraseña se encontró en una filtración y no se puede usar. Por favor, restablecé tu contraseña.',
    form_password_size_in_bytes_exceeded:
      'Tu contraseña ha excedido el número máximo de bytes permitidos. Por favor, acortala o eliminá algunos caracteres especiales.',
    form_password_validation_failed: 'Contraseña incorrecta',
    form_username_invalid_character:
      'Tu nombre de usuario contiene caracteres inválidos. Por favor, usá solo letras, números y guiones bajos.',
    form_username_invalid_length: 'Tu nombre de usuario debe tener entre {{min_length}} y {{max_length}} caracteres.',
    identification_deletion_failed: 'No podés eliminar tu última identificación.',
    not_allowed_access:
      "La dirección de correo electrónico o el número de teléfono no está permitido para registrarse. Esto puede deberse al uso de '+', '=', '#' o '.' en tu dirección de correo electrónico, el uso de un dominio conectado a un servicio de correo electrónico temporal o la exclusión explícita. Si cree que se trata de un error, póngase en contacto con el soporte.",
    organization_domain_blocked: 'Este es un dominio de proveedor de correo bloqueado. Por favor, usá otro.',
    organization_domain_common: 'Este es un dominio de proveedor de correo común. Por favor, usá otro.',
    organization_domain_exists_for_enterprise_connection: 'Este dominio ya se utiliza para el SSO de tu organización.',
    organization_membership_quota_exceeded:
      'Has alcanzado el límite de membresías en organizaciones, incluyendo invitaciones pendientes.',
    organization_minimum_permissions_needed:
      'Debe haber al menos un miembro de la organización con los permisos mínimos requeridos.',
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'Ya hay una clave de acceso registrada en este dispositivo.',
    passkey_not_supported: 'Las claves de acceso no son compatibles con este dispositivo.',
    passkey_pa_not_supported: 'El registro requiere un autenticador de plataforma, pero el dispositivo no lo soporta.',
    passkey_registration_cancelled: 'El registro de la clave de acceso fue cancelado o agotó el tiempo de espera.',
    passkey_retrieval_cancelled: 'La verificación de la clave de acceso fue cancelada o agotó el tiempo de espera.',
    passwordComplexity: {
      maximumLength: 'menos de {{length}} caracteres',
      minimumLength: '{{length}} o más caracteres',
      requireLowercase: 'una letra minúscula',
      requireNumbers: 'un número',
      requireSpecialCharacter: 'un carácter especial',
      requireUppercase: 'una letra mayúscula',
      sentencePrefix: 'Tu contraseña debe contener',
    },
    phone_number_exists: 'Este número de teléfono ya está en uso. Por favor, probá con otro.',
    session_exists: 'Ya has iniciado sesión',
    web3_missing_identifier: 'No se encontró una extensión de cartera Web3. Por favor, instalá una para continuar.',
    zxcvbn: {
      couldBeStronger: 'Tu contraseña funciona, pero podría ser más fuerte. Intentá agregar más caracteres.',
      goodPassword: 'Tu contraseña cumple con todos los requisitos necesarios.',
      notEnough: 'Tu contraseña no es lo suficientemente fuerte.',
      suggestions: {
        allUppercase: 'Usá mayúsculas en algunas letras, pero no en todas.',
        anotherWord: 'Agregá más palabras que sean menos comunes.',
        associatedYears: 'Evitá años que estén asociados contigo.',
        capitalization: 'Usá mayúsculas en más de la primera letra.',
        dates: 'Evitá fechas y años que estén asociados contigo.',
        l33t: 'Evitá sustituciones de letras predecibles como "@" en lugar de "a".',
        longerKeyboardPattern: 'Usá patrones de teclado más largos y cambiá la dirección de tecleo varias veces.',
        noNeed: 'Podés crear contraseñas fuertes sin usar símbolos, números o letras mayúsculas.',
        pwned: 'Si usás esta contraseña en otros sitios, deberías cambiarla.',
        recentYears: 'Evitá años recientes.',
        repeated: 'Evitá palabras y caracteres repetidos.',
        reverseWords: 'Evitá las versiones invertidas de palabras comunes.',
        sequences: 'Evitá secuencias comunes de caracteres.',
        useWords: 'Usá varias palabras, pero evitá frases comunes.',
      },
      warnings: {
        common: 'Esta es una contraseña común.',
        commonNames: 'Los nombres y apellidos comunes son fáciles de adivinar.',
        dates: 'Las fechas son fáciles de adivinar.',
        extendedRepeat: 'Los patrones de caracteres repetidos como "abcabcabc" son fáciles de adivinar.',
        keyPattern: 'Los patrones cortos de teclado son fáciles de adivinar.',
        namesByThemselves: 'Los nombres o apellidos por sí solos son fáciles de adivinar.',
        pwned: 'Tu contraseña se vio comprometida en una filtración de datos en Internet.',
        recentYears: 'Los años recientes son fáciles de adivinar.',
        sequences: 'Secuencias comunes de caracteres como "abc" son fáciles de adivinar.',
        similarToCommon: 'Esto es similar a una contraseña común.',
        simpleRepeat: 'Caracteres repetidos como "aaa" son fáciles de adivinar.',
        straightRow: 'Filas rectas de teclas en tu teclado son fáciles de adivinar.',
        topHundred: 'Esta es una contraseña frecuentemente usada.',
        topTen: 'Esta es una contraseña muy usada.',
        userInputs: 'No debe haber ningún dato personal o relacionado con la página.',
        wordByItself: 'Palabras solas son fáciles de adivinar.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Agregar cuenta',
    action__closeUserMenu: undefined,
    action__manageAccount: 'Gestionar cuenta',
    action__openUserMenu: undefined,
    action__signOut: 'Cerrar sesión',
    action__signOutAll: 'Cerrar sesión en todas las cuentas',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: '¡Copiado!',
      actionLabel__copy: 'Copiar todo',
      actionLabel__download: 'Descargar .txt',
      actionLabel__print: 'Imprimir',
      infoText1: 'Se habilitarán códigos de respaldo para esta cuenta.',
      infoText2:
        'Mantené los códigos de respaldo en secreto y guardalos de forma segura. Podés regenerarlos si sospechás que han sido comprometidos.',
      subtitle__codelist: 'Guardalos de forma segura y mantenelos en secreto.',
      successMessage:
        'Los códigos de respaldo están habilitados. Podés usar uno de ellos para ingresar a tu cuenta si perdés el acceso a tu dispositivo de autenticación. Cada código solo puede usarse una vez.',
      successSubtitle:
        'Podés usar uno de ellos para ingresar a tu cuenta si perdés el acceso a tu dispositivo de autenticación.',
      title: 'Agregar verificación con código de respaldo',
      title__codelist: 'Códigos de respaldo',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: 'Seleccioná un proveedor para conectar tu cuenta.',
      formHint__noAccounts: 'No hay proveedores externos de cuenta disponibles.',
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás usar esta cuenta conectada y las funciones dependientes dejarán de funcionar.',
        successMessage: '{{connectedAccount}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar cuenta conectada',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'El proveedor ha sido agregado a tu cuenta',
      title: 'Agregar cuenta conectada',
    },
    deletePage: {
      actionDescription: 'Escribí "Eliminar cuenta" abajo para continuar.',
      confirm: 'Eliminar cuenta',
      messageLine1:
        '¿Estás seguro de que querés eliminar tu cuenta? Es posible que se conserven algunos datos asociados. Para solicitar la eliminación completa de datos, contactá al soporte.',
      messageLine2: 'Esta acción es permanente e irreversible.',
      title: 'Eliminar cuenta',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Se enviará un correo con un código de verificación a este correo electrónico.',
        formSubtitle: 'Ingresá el código de verificación enviado a {{identifier}}',
        formTitle: 'Código de verificación',
        resendButton: '¿No recibiste un código? Reenviar',
        successMessage: 'El correo {{identifier}} ha sido agregado a tu cuenta.',
      },
      emailLink: {
        formHint: 'Se enviará un correo con un enlace de verificación a este correo electrónico.',
        formSubtitle: 'Hacé clic en el enlace de verificación enviado a {{identifier}}',
        formTitle: 'Enlace de verificación',
        resendButton: '¿No recibiste un enlace? Reenviar',
        successMessage: 'El correo {{identifier}} ha sido agregado a tu cuenta.',
      },
      enterpriseSSOLink: {
        formButton: 'Hacé clic para iniciar sesión',
        formSubtitle: 'Completá el inicio de sesión con {{identifier}}',
      },
      formHint: 'Necesitás verificar este correo electrónico antes de poder agregarlo a tu cuenta.',
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás iniciar sesión usando este correo electrónico.',
        successMessage: '{{emailAddress}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar correo electrónico',
      },
      title: 'Agregar correo electrónico',
      verifyTitle: 'Verificar correo electrónico',
    },
    formButtonPrimary__add: 'Agregar',
    formButtonPrimary__continue: 'Continuar',
    formButtonPrimary__finish: 'Finalizar',
    formButtonPrimary__remove: 'Eliminar',
    formButtonPrimary__save: 'Guardar',
    formButtonReset: 'Cancelar',
    mfaPage: {
      formHint: 'Seleccioná un método para agregar.',
      title: 'Agregar verificación de dos pasos',
    },
    mfaPhoneCodePage: {
      backButton: 'Usar número existente',
      primaryButton__addPhoneNumber: 'Agregar número de teléfono',
      removeResource: {
        messageLine1: '{{identifier}} dejará de recibir códigos de verificación al iniciar sesión.',
        messageLine2: 'Tu cuenta podría no estar tan segura. ¿Estás seguro de querer continuar?',
        successMessage: 'La verificación de dos pasos mediante código SMS ha sido eliminada para {{mfaPhoneCode}}',
        title: 'Eliminar verificación de dos pasos',
      },
      subtitle__availablePhoneNumbers:
        'Seleccioná un número de teléfono existente para registrar la verificación de dos pasos por código SMS o agregá uno nuevo.',
      subtitle__unavailablePhoneNumbers:
        'No hay números de teléfono disponibles para registrar la verificación de dos pasos por código SMS, por favor, agregá uno nuevo.',
      successMessage1:
        'Al iniciar sesión, deberás ingresar un código de verificación enviado a este número como un paso adicional.',
      successMessage2:
        'Guardá estos códigos de respaldo y almacenalos en un lugar seguro. Si perdés el acceso a tu dispositivo de autenticación, podés usar los códigos de respaldo para ingresar.',
      successTitle: 'Verificación por código SMS habilitada',
      title: 'Agregar verificación por código SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Escaneá el código QR en su lugar',
        buttonUnableToScan__nonPrimary: '¿No podés escanear el código QR?',
        infoText__ableToScan:
          'Configurá un nuevo método de ingreso en tu aplicación autenticadora y escaneá el siguiente código QR para vincularlo a tu cuenta.',
        infoText__unableToScan:
          'Configurá un nuevo método de ingreso en tu autenticadora e ingresá la clave proporcionada abajo.',
        inputLabel__unableToScan1:
          'Asegurate de que las contraseñas de un solo uso basadas en tiempo estén habilitadas, luego completá la vinculación de tu cuenta.',
        inputLabel__unableToScan2:
          'Alternativamente, si tu autenticadora soporta URIs TOTP, también podés copiar la URI completa.',
      },
      removeResource: {
        messageLine1: 'Los códigos de verificación de esta autenticadora ya no serán requeridos al iniciar sesión.',
        messageLine2: 'Tu cuenta podría no estar tan segura. ¿Estás seguro de querer continuar?',
        successMessage: 'La verificación de dos pasos mediante aplicación autenticadora ha sido eliminada.',
        title: 'Eliminar verificación de dos pasos',
      },
      successMessage:
        'La verificación de dos pasos está habilitada. Al iniciar sesión, deberás ingresar un código de verificación de esta autenticadora como paso adicional.',
      title: 'Agregar aplicación autenticadora',
      verifySubtitle: 'Ingresá el código de verificación generado por tu autenticadora',
      verifyTitle: 'Código de verificación',
    },
    mobileButton__menu: 'Menú',
    navbar: {
      account: 'Perfil',
      apiKeys: undefined,
      billing: undefined,
      description: 'Gestioná la información de tu cuenta.',
      security: 'Seguridad',
      title: 'Cuenta',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} será eliminado de esta cuenta.',
        title: 'Eliminar clave de acceso',
      },
      subtitle__rename: 'Podés cambiar el nombre de la clave de acceso para facilitar su identificación.',
      title__rename: 'Renombrar clave de acceso',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Se recomienda cerrar sesión en todos los demás dispositivos que hayan usado tu antigua contraseña.',
      readonly:
        'Actualmente, tu contraseña no puede ser editada porque solo podés ingresar mediante la conexión empresarial.',
      successMessage__set: 'Tu contraseña ha sido establecida.',
      successMessage__signOutOfOtherSessions: 'Todos los demás dispositivos han sido cerrados sesión.',
      successMessage__update: 'Tu contraseña ha sido actualizada.',
      title__set: 'Establecer contraseña',
      title__update: 'Actualizar contraseña',
    },
    phoneNumberPage: {
      infoText:
        'Se enviará un mensaje de texto con un código de verificación a este número. Pueden aplicarse tarifas de mensaje y datos.',
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás iniciar sesión usando este número de teléfono.',
        successMessage: '{{phoneNumber}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar número de teléfono',
      },
      successMessage: '{{identifier}} ha sido agregado a tu cuenta.',
      title: 'Agregar número de teléfono',
      verifySubtitle: 'Ingresá el código de verificación enviado a {{identifier}}',
      verifyTitle: 'Verificar número de teléfono',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'Tamaño recomendado 1:1, hasta 10MB.',
      imageFormDestructiveActionSubtitle: 'Eliminar',
      imageFormSubtitle: 'Subir',
      imageFormTitle: 'Imagen de perfil',
      readonly: 'La información de tu perfil fue proporcionada por la conexión empresarial y no puede ser editada.',
      successMessage: 'Tu perfil ha sido actualizado.',
      title: 'Actualizar perfil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Cerrar sesión en dispositivo',
        title: 'Dispositivos activos',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Reconectar',
        actionLabel__reauthorize: 'Autorizar ahora',
        destructiveActionTitle: 'Eliminar',
        primaryButton: 'Conectar cuenta',
        subtitle__disconnected: 'Esta cuenta ha sido desconectada.',
        subtitle__reauthorize:
          'Los permisos requeridos han sido actualizados y podrías experimentar funcionalidad limitada. Por favor, reautoriza esta aplicación para evitar inconvenientes',
        title: 'Cuentas conectadas',
      },
      dangerSection: {
        deleteAccountButton: 'Eliminar cuenta',
        title: 'Eliminar cuenta',
      },
      emailAddressesSection: {
        destructiveAction: 'Eliminar correo',
        detailsAction__nonPrimary: 'Establecer como principal',
        detailsAction__primary: 'Completar verificación',
        detailsAction__unverified: 'Verificar',
        primaryButton: 'Agregar correo electrónico',
        title: 'Correos electrónicos',
      },
      enterpriseAccountsSection: {
        title: 'Cuentas empresariales',
      },
      headerTitle__account: 'Detalles del perfil',
      headerTitle__security: 'Seguridad',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Regenerar',
          headerTitle: 'Códigos de respaldo',
          subtitle__regenerate:
            'Obtené un nuevo conjunto de códigos de respaldo seguros. Los códigos anteriores serán eliminados y no podrán usarse.',
          title__regenerate: 'Regenerar códigos de respaldo',
        },
        phoneCode: {
          actionLabel__setDefault: 'Establecer como predeterminado',
          destructiveActionLabel: 'Eliminar',
        },
        primaryButton: 'Agregar verificación de dos pasos',
        title: 'Verificación de dos pasos',
        totp: {
          destructiveActionTitle: 'Eliminar',
          headerTitle: 'Aplicación autenticadora',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Eliminar',
        menuAction__rename: 'Renombrar',
        primaryButton: 'Agregar una clave de acceso',
        title: 'Claves de acceso',
      },
      passwordSection: {
        primaryButton__setPassword: 'Establecer contraseña',
        primaryButton__updatePassword: 'Actualizar contraseña',
        title: 'Contraseña',
      },
      phoneNumbersSection: {
        destructiveAction: 'Eliminar número de teléfono',
        detailsAction__nonPrimary: 'Establecer como principal',
        detailsAction__primary: 'Completar verificación',
        detailsAction__unverified: 'Verificar número de teléfono',
        primaryButton: 'Agregar número de teléfono',
        title: 'Números de teléfono',
      },
      profileSection: {
        primaryButton: 'Actualizar perfil',
        title: 'Perfil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Establecer nombre de usuario',
        primaryButton__updateUsername: 'Actualizar nombre de usuario',
        title: 'Nombre de usuario',
      },
      web3WalletsSection: {
        destructiveAction: 'Eliminar cartera',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'Conectar cartera',
        title: 'Carteras Web3',
      },
    },
    usernamePage: {
      successMessage: 'Tu nombre de usuario ha sido actualizado.',
      title__set: 'Establecer nombre de usuario',
      title__update: 'Actualizar nombre de usuario',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás iniciar sesión usando esta cartera Web3.',
        successMessage: '{{web3Wallet}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar cartera Web3',
      },
      subtitle__availableWallets: 'Seleccioná una cartera Web3 para conectar a tu cuenta.',
      subtitle__unavailableWallets: 'No hay carteras Web3 disponibles.',
      successMessage: 'La cartera ha sido agregada a tu cuenta.',
      title: 'Agregar cartera Web3',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Iniciar sesión',
      actionText: '¿Ya tenés acceso?',
      formButton: 'Unirse a la lista de espera',
      subtitle: 'Ingresá tu correo electrónico y te avisaremos cuando tu lugar esté listo',
      title: 'Unirse a la lista de espera',
    },
    success: {
      message: 'Serás redirigido pronto...',
      subtitle: 'Nos pondremos en contacto cuando tu lugar esté listo',
      title: '¡Gracias por unirte a la lista de espera!',
    },
  },
} as const;
